/**
 * Newsletter signup that posts to an external webhook (Make.com) instead of the
 * Shopify customer form handler.
 *
 * The markup is rendered by `blocks/email-signup-makecom.liquid`: one `<form>`
 * with `data-form`, plus a success and an error container. Field names follow the
 * original Shopify block (`contact[email]`, `contact[note][birthday_day]`, …) so
 * the block stays a drop-in copy; the payload flattens them to the last bracket
 * segment, e.g. `contact[note][birthday_day]` is sent as `birthday_day`.
 */

/**
 * `contact[note][birthday_day]` -> `birthday_day`, `agreement` -> `agreement`.
 *
 * @param {string} name
 * @returns {string}
 */
function payloadKey(name) {
  const segments = name.match(/[^[\]]+/g);
  return segments ? segments[segments.length - 1] : name;
}

class EmailSignupMakecom extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {boolean} */
  #submitting = false;

  connectedCallback() {
    this.#form = this.querySelector('[data-form]');
    if (!this.#form) return;

    // Re-adding the same listener reference is a no-op, so reconnecting is safe.
    this.#form.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  #onSubmit = async (event) => {
    // The browser has already run native validation at this point — `submit`
    // does not fire for an invalid form.
    event.preventDefault();
    if (this.#submitting || !this.#form) return;

    const endpoint = this.dataset.endpoint;

    if (!endpoint) {
      console.warn('[email-signup-makecom] No webhook URL set in the block settings.');
      this.#showError();
      return;
    }

    const submitButton = this.#form.querySelector('button:not([type="button"])');

    this.#submitting = true;
    this.querySelector('[data-error]')?.setAttribute('hidden', '');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.#payload()),
      });

      if (!response.ok) {
        throw new Error(`Webhook responded with ${response.status} ${response.statusText}`);
      }

      this.#showSuccess();
    } catch (error) {
      // A TypeError here is the browser refusing the response, not the server
      // rejecting it — almost always a missing Access-Control-Allow-Origin.
      console.error('[email-signup-makecom] POST to %s failed:', endpoint, error);
      this.#showError();
    } finally {
      this.#submitting = false;
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
    }
  };

  /** @returns {Record<string, unknown>} */
  #payload() {
    /** @type {Record<string, unknown>} */
    const payload = { source_url: window.location.href };
    if (!this.#form) return payload;

    const controls = /** @type {Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>} */ (
      Array.from(this.#form.querySelectorAll('input, textarea, select'))
    ).filter((control) => control.name && !control.disabled);

    for (const control of controls) {
      const key = payloadKey(control.name);

      if (control instanceof HTMLInputElement) {
        if (control.type === 'checkbox') {
          payload[key] = control.checked;
          continue;
        }

        if (control.type === 'radio' && !control.checked) continue;
      }

      payload[key] = control.value.trim();
    }

    return payload;
  }

  #showSuccess() {
    const success = this.querySelector('[data-success]');

    this.#form?.reset();
    this.#form?.setAttribute('hidden', '');
    this.querySelector('[data-error]')?.setAttribute('hidden', '');

    if (success instanceof HTMLElement) {
      success.removeAttribute('hidden');
      success.focus();
      this.#toast(success.dataset.toastMessage, 'success');
    }
  }

  #showError() {
    const error = this.querySelector('[data-error]');
    if (!(error instanceof HTMLElement)) return;

    error.removeAttribute('hidden');
    error.focus();
    this.#toast(error.dataset.toastMessage, 'error');
  }

  /**
   * The toast manager only scans the DOM on load, so an AJAX result has to
   * announce itself. Silently skipped when the toast script is not on the page.
   *
   * @param {string | undefined} message
   * @param {'success' | 'error'} type
   */
  #toast(message, type) {
    if (!message) return;
    window.NotificationToast?.show(message, type);
  }
}

if (!customElements.get('email-signup-makecom')) {
  customElements.define('email-signup-makecom', EmailSignupMakecom);
}
